/**
 * tb-advisory-banner — route-scoped, audience-targeted platform advisory banner
 * (Platform advisories v1).
 *
 * CONTRACT (pinned, see the lane brief): advisories arrive as ORDINARY notifications with
 * `category: 'ADVISORY'` on the EXISTING NotificationService stream/list — no new transport.
 * This component consumes `NotificationService.dropdownItems` (the same signal the bell dropdown
 * and toast tray read) filtered to ADVISORY, within the active window (`payload.activeFrom` /
 * `payload.activeUntil`), and not dismissed. Dismiss calls the existing, real
 * `NotificationService.dismiss()` endpoint (persisted server-side) — never a sessionStorage hack
 * (contrast with toybank-app's FactorExpiryBannerComponent, which is local-only by design for a
 * different, session-scoped concern).
 *
 * ROUTE SCOPING (the new mechanism, DESIGN.md-adjacent): CRITICAL advisories show on EVERY page.
 * WARN/INFO advisories show only when the current route's backing services (the `services` input
 * — see toybank-app's route-services.ts) intersect the advisory's `payload.affectedServices`. The
 * host shell computes `services` from the current route and passes it in; this component does no
 * routing of its own.
 *
 * GRACEFUL DEGRADATION: the backend (ADR-0217, `AdvisoryEventListener.java` + notifications
 * migration V31) attaches the structured `payload` (title/message/affectedServices/
 * activeFrom/activeUntil) to every ADVISORY notification it writes, so the payload branch of
 * `resolveAdvisory` below is the normal path. The `summary` fallback stays for pre-V31 rows and
 * any non-conforming payload: rather than exclude such an advisory outright (which would silently
 * break even the CRITICAL-everywhere guarantee), it renders `summary` as the title with no
 * route-scoping data and no expiry — CRITICAL still shows everywhere; WARN/INFO never
 * route-matches without a real `affectedServices` list (the same, already-correct behavior an
 * advisory with an explicitly empty `affectedServices` gets).
 *
 * Stacking: CRITICAL first, then WARN, then INFO; each severity keeps arrival order (newest
 * first, mirroring dropdownItems). CRITICAL rows are visually sticky (DESIGN.md finance/negative
 * treatment) so they can't scroll out of view.
 *
 * Studied for idiom: toybank-app's FactorExpiryBannerComponent (severity-styled banner-stack row,
 * dismiss button, role="alert") and the shell's logout-error-banner (inline dismiss affordance).
 * Severity chrome (color + paired text label) delegates to tb-severity-chip, same as
 * tb-notification-row/-toast — color is never the only signal (DESIGN.md §8).
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { TranslocoDirective, provideTranslocoScope } from '@jsverse/transloco';
import { Notification, NotificationSeverity } from './notification.model';
import { NotificationService } from './notification.service';
import { TbSeverityChipComponent } from './tb-severity-chip.component';

/** What the banner actually renders/scopes on — resolved from the structured `payload` (the
 *  normal path) or the plain `summary` fallback for degraded rows (see the module doc above). */
interface ResolvedAdvisory {
  readonly title: string;
  readonly message: string;
  readonly affectedServices: readonly string[];
  /** epoch ms, or null when no expiry data is available (degraded summary-only rows). */
  readonly activeUntilMs: number | null;
  /** epoch ms, or null when not yet active is undeterminable (no data, or not supplied). */
  readonly activeFromMs: number | null;
}

interface VisibleAdvisory {
  readonly n: Notification;
  readonly resolved: ResolvedAdvisory;
}

const SEVERITY_RANK: Record<NotificationSeverity, number> = {
  CRITICAL: 2,
  WARN: 1,
  INFO: 0,
};

const SEVERITY_ICON: Record<NotificationSeverity, string> = {
  CRITICAL: 'error',
  WARN: 'warning',
  INFO: 'info',
};

/**
 * Resolves what to render/scope for one ADVISORY notification, or null if there is truly nothing
 * usable (no valid payload AND no summary — should not happen in practice, but fails closed
 * rather than rendering a blank banner row).
 */
function resolveAdvisory(n: Notification): ResolvedAdvisory | null {
  const p = n.payload;
  if (
    p &&
    typeof p.title === 'string' && p.title.trim() &&
    typeof p.message === 'string' && p.message.trim() &&
    typeof p.activeUntil === 'string' && !Number.isNaN(Date.parse(p.activeUntil))
  ) {
    const activeFromMs = p.activeFrom && !Number.isNaN(Date.parse(p.activeFrom))
      ? Date.parse(p.activeFrom)
      : null;
    return {
      title: p.title,
      message: p.message,
      affectedServices: p.affectedServices ?? [],
      activeUntilMs: Date.parse(p.activeUntil),
      activeFromMs,
    };
  }

  // Fallback for pre-V31 / non-conforming rows (no structured payload) — see the module doc's
  // GRACEFUL DEGRADATION section. No route-scoping data, no expiry data.
  const summary = (n.summary ?? '').trim();
  if (!summary) return null;
  return { title: summary, message: '', affectedServices: [], activeUntilMs: null, activeFromMs: null };
}

function isWithinActiveWindow(resolved: ResolvedAdvisory, nowMs: number): boolean {
  if (resolved.activeUntilMs !== null && resolved.activeUntilMs < nowMs) return false;
  if (resolved.activeFromMs !== null && resolved.activeFromMs > nowMs) return false;
  return true;
}

@Component({
  selector: 'tb-advisory-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, TbSeverityChipComponent],
  providers: [provideTranslocoScope('ui')],
  template: `
    <ng-container *transloco="let t; read: 'ui.advisoryBanner'">
    @for (item of visibleAdvisories(); track item.n.id) {
      <div
        class="advisory-banner"
        [attr.data-sev]="item.n.severity"
        role="alert"
        aria-live="assertive"
        [attr.data-testid]="'advisory-banner-' + item.n.id"
      >
        <span class="advisory-icon material-symbols-outlined" aria-hidden="true">{{ severityIcon(item.n.severity) }}</span>
        <tb-severity-chip [severity]="item.n.severity" />
        <span class="advisory-text">
          <strong class="advisory-title">{{ item.resolved.title }}</strong>
          @if (item.resolved.message) {
            <span class="advisory-message">{{ item.resolved.message }}</span>
          }
        </span>
        <button
          type="button"
          class="advisory-dismiss"
          [disabled]="dismissingIds().has(item.n.id)"
          [attr.data-testid]="'advisory-dismiss-' + item.n.id"
          [attr.aria-label]="t('dismissAria', { title: item.resolved.title })"
          (click)="dismiss(item.n.id)"
        >
          <span class="material-symbols-outlined" aria-hidden="true">close</span>
        </button>
      </div>
    }
    </ng-container>
  `,
  styles: `
    :host { display: block; }

    .advisory-banner {
      display: flex; align-items: center; gap: 0.6rem;
      padding: 0.6rem 1rem; font-size: 0.85rem; font-weight: 500;
      border-bottom: 1px solid transparent;
    }
    .advisory-banner[data-sev='INFO'] {
      background: color-mix(in srgb, var(--tb-status-info, #7aa2f7) 14%, transparent);
      border-bottom-color: color-mix(in srgb, var(--tb-status-info, #7aa2f7) 35%, transparent);
      color: var(--tb-status-info, #7aa2f7);
    }
    .advisory-banner[data-sev='WARN'] {
      background: color-mix(in srgb, var(--tb-status-warning, #e0af68) 18%, transparent);
      border-bottom-color: color-mix(in srgb, var(--tb-status-warning, #e0af68) 40%, transparent);
      color: var(--tb-status-warning, #e0af68);
    }
    .advisory-banner[data-sev='CRITICAL'] {
      background: color-mix(in srgb, var(--tb-finance-negative, #f7768e) 18%, transparent);
      border-bottom-color: color-mix(in srgb, var(--tb-finance-negative, #f7768e) 40%, transparent);
      color: var(--tb-finance-negative, #f7768e);
      /* CRITICAL = sticky: pinned so it cannot scroll out of view while it is active. */
      position: sticky; top: 0; z-index: 95;
    }

    .advisory-icon { font-size: 1.1rem; flex-shrink: 0; }

    .advisory-text {
      flex: 1; min-width: 0;
      display: flex; align-items: baseline; flex-wrap: wrap; gap: 0.4rem;
      color: inherit;
    }
    .advisory-title { font-weight: 700; }
    .advisory-message { color: inherit; opacity: 0.92; }

    .advisory-dismiss {
      background: none; border: none; cursor: pointer; padding: 0.15rem;
      color: inherit; display: flex; align-items: center; opacity: 0.7;
      justify-content: center; flex-shrink: 0;
      min-width: var(--tb-touch, 48px); min-height: var(--tb-touch, 48px);
    }
    .advisory-dismiss:hover:not(:disabled) { opacity: 1; }
    .advisory-dismiss:disabled { opacity: 0.4; cursor: default; }
    .advisory-dismiss:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
    }
    .advisory-dismiss .material-symbols-outlined { font-size: 1rem; }
  `,
})
export class TbAdvisoryBannerComponent {
  private readonly notifService = inject(NotificationService);

  /** The current route's backing-service ids (see toybank-app's route-services.ts). Empty by
   *  default — an unmapped/unset route still shows CRITICAL advisories, per the display rule. */
  readonly services = input<readonly string[]>([]);

  /** Ids with an in-flight dismiss request — disables the button against double-submit. */
  readonly dismissingIds = signal<ReadonlySet<string>>(new Set());

  readonly visibleAdvisories = computed<VisibleAdvisory[]>(() => {
    const routeServices = new Set(this.services());
    const now = Date.now();

    const candidates: VisibleAdvisory[] = [];
    for (const n of this.notifService.dropdownItems()) {
      if (n.category !== 'ADVISORY' || n.dismissedAt) continue;
      const resolved = resolveAdvisory(n);
      if (!resolved || !isWithinActiveWindow(resolved, now)) continue;
      candidates.push({ n, resolved });
    }

    const scoped = candidates.filter(({ n, resolved }) => {
      if (n.severity === 'CRITICAL') return true;
      return resolved.affectedServices.some((svc) => routeServices.has(svc));
    });

    // Stable sort (ES2019+): CRITICAL first, then WARN, then INFO; ties keep arrival order.
    return [...scoped].sort((a, b) => SEVERITY_RANK[b.n.severity] - SEVERITY_RANK[a.n.severity]);
  });

  severityIcon(severity: NotificationSeverity): string {
    return SEVERITY_ICON[severity];
  }

  dismiss(id: string): void {
    if (this.dismissingIds().has(id)) return;
    this.dismissingIds.update((prev) => new Set(prev).add(id));
    this.notifService.dismiss(id).subscribe({
      next: () => {
        // Pessimistic (DESIGN.md §7.5): only reflect the dismiss once the server acknowledges it.
        // Mutates the SAME shared dropdownItems signal the bell/toast read, so dismissing an
        // advisory from the banner also clears it from those surfaces — one source of truth, no
        // sessionStorage-only local state.
        this.notifService.dropdownItems.update((prev) => prev.filter((x) => x.id !== id));
        this.clearInFlight(id);
      },
      error: () => this.clearInFlight(id),
    });
  }

  private clearInFlight(id: string): void {
    this.dismissingIds.update((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }
}
